import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import {
  SMART_COLORS as COLORS,
  smartActionButtonSx,
  smartCardSx as cardSx,
  smartTableContainerSx,
  smartTableHeadSx,
  smartTableSx,
} from '../../theme/smartStableTheme';
import SettingsPageHeader from '../../components/SettingsPageHeader';
import SmartTablePagination, { useSmartPagination } from '../../components/SmartTablePagination';

const apiUrl = process.env.REACT_APP_API_URL;
const emptyForm = { colorArabic: '', colorEnglish: '' };

const HorseColorList = () => {
  const { t, i18n } = useTranslation();
  const [colors, setColors] = useState([]);
  const [editColorId, setEditColorId] = useState(null);
  const [deleteColorId, setDeleteColorId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const isRtl = String(i18n.language || '').startsWith('ar');

  const fetchColors = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${apiUrl}/horse_colors`);
      setColors(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(err?.response?.data?.error || t('horse_colors_load_error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchColors();
  }, [fetchColors]);

  const filteredColors = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return colors;
    return colors.filter((color) => (
      String(color.color_arabic || '').toLowerCase().includes(term)
      || String(color.color_english || '').toLowerCase().includes(term)
    ));
  }, [colors, search]);
  const {
    page,
    rowsPerPage,
    paginatedItems: paginatedColors,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useSmartPagination(filteredColors, [search]);

  const resetForm = useCallback(() => {
    setEditColorId(null);
    setIsAddDialogOpen(false);
    setForm(emptyForm);
  }, []);

  const openAddDialog = useCallback(() => {
    setMessage('');
    setError('');
    setForm(emptyForm);
    setIsAddDialogOpen(true);
  }, []);

  const openEditDialog = useCallback((color) => {
    setMessage('');
    setError('');
    setEditColorId(color.color_id);
    setForm({
      colorArabic: color.color_arabic || '',
      colorEnglish: color.color_english || '',
    });
  }, []);

  const validateForm = useCallback(() => {
    if (!form.colorArabic.trim() || !form.colorEnglish.trim()) {
      setError(t('horse_color_fields_required'));
      return false;
    }
    return true;
  }, [form.colorArabic, form.colorEnglish, t]);

  const saveColor = useCallback(async () => {
    if (!validateForm()) return;
    setSaving(true);
    setError('');
    setMessage('');
    const payload = {
      color_arabic: form.colorArabic.trim(),
      color_english: form.colorEnglish.trim(),
    };
    try {
      if (editColorId) {
        await axios.put(`${apiUrl}/horse_colors/${editColorId}`, payload);
        setMessage(t('horse_color_updated_successfully'));
      } else {
        await axios.post(`${apiUrl}/horse_colors`, payload);
        setMessage(t('horse_color_added_successfully'));
      }
      resetForm();
      await fetchColors();
    } catch (err) {
      setError(err?.response?.data?.error || (editColorId ? t('horse_color_update_error') : t('horse_color_add_error')));
    } finally {
      setSaving(false);
    }
  }, [editColorId, fetchColors, form.colorArabic, form.colorEnglish, resetForm, t, validateForm]);

  const handleDelete = useCallback(async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await axios.delete(`${apiUrl}/horse_colors/${deleteColorId}`);
      setDeleteColorId(null);
      setMessage(t('horse_color_deleted_successfully'));
      await fetchColors();
    } catch (err) {
      setError(err?.response?.data?.error || t('horse_color_delete_error'));
    } finally {
      setSaving(false);
    }
  }, [deleteColorId, fetchColors, t]);

  return (
    <Box
      className="smart-page-container"
      sx={{ bgcolor: COLORS.page, minHeight: '100vh', maxWidth: 'var(--app-page-max)', mx: 'auto' }}
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      <SettingsPageHeader
        title={t('horse_colors')}
        subtitle={t('horse_colors_hint')}
        count={colors.length}
        actions={(
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAddDialog}
            sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover }, fontWeight: 900 }}
          >
            {t('add_color')}
          </Button>
        )}
      />

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {message && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>
          {message}
        </Alert>
      )}

      <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ p: 2, borderBottom: `1px solid ${COLORS.line}` }}>
          <TextField
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            label={t('search_colors')}
            InputProps={{ startAdornment: <SearchIcon sx={{ color: COLORS.muted, mr: isRtl ? 0 : 1, ml: isRtl ? 1 : 0 }} /> }}
            sx={{ maxWidth: { sm: 360 } }}
            fullWidth
          />
        </Stack>
        <TableContainer sx={smartTableContainerSx}>
          <Table size="small" sx={smartTableSx}>
            <TableHead sx={smartTableHeadSx}>
              <TableRow>
                <TableCell align="center" sx={{ width: 72 }}>#</TableCell>
                <TableCell>{t('color_arabic')}</TableCell>
                <TableCell>{t('color_english')}</TableCell>
                <TableCell align="center" sx={{ width: 150 }}>{t('actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedColors.map((color, index) => (
                <TableRow key={color.color_id} hover>
                  <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell sx={{ color: COLORS.text, fontWeight: 900, minWidth: 180 }}>{color.color_arabic}</TableCell>
                  <TableCell sx={{ minWidth: 180 }}>{color.color_english}</TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Tooltip title={t('edit_color')}>
                        <IconButton onClick={() => openEditDialog(color)} sx={smartActionButtonSx(COLORS.blue, COLORS.blueSoft)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('delete_color')}>
                        <IconButton onClick={() => setDeleteColorId(color.color_id)} sx={smartActionButtonSx(COLORS.rose, COLORS.roseSoft)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {!filteredColors.length && !loading && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography sx={{ color: COLORS.muted, py: 3, textAlign: 'center' }}>
                      {search ? t('no_search_results') : t('no_colors_found')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <SmartTablePagination
          count={filteredColors.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <Dialog open={Boolean(editColorId) || isAddDialogOpen} onClose={resetForm} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: COLORS.text, fontWeight: 900 }}>{editColorId ? t('edit_color') : t('add_color')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField fullWidth label={t('color_arabic')} value={form.colorArabic} onChange={(event) => setForm((current) => ({ ...current, colorArabic: event.target.value }))} />
            <TextField fullWidth label={t('color_english')} value={form.colorEnglish} onChange={(event) => setForm((current) => ({ ...current, colorEnglish: event.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={resetForm} sx={{ color: COLORS.muted, fontWeight: 900 }}>
            {t('cancel')}
          </Button>
          <Button onClick={saveColor} variant="contained" disabled={saving} sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover }, fontWeight: 900 }}>
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteColorId)} onClose={() => setDeleteColorId(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: COLORS.text, fontWeight: 900 }}>{t('delete_confirmation')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('delete_color_confirmation')}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteColorId(null)} sx={{ color: COLORS.muted, fontWeight: 900 }}>
            {t('cancel')}
          </Button>
          <Button onClick={handleDelete} disabled={saving} sx={{ color: COLORS.rose, fontWeight: 900 }}>
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HorseColorList;
